import { useState } from 'react'
import { router } from '@inertiajs/react'
import { FilterParams } from '../../types'

interface Props {
  teamId: number
  filters: FilterParams
}

const CADENCE_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
]

const STATE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
]

const WEEKS_OPTIONS = [
  { value: 4, label: 'Last 4 weeks' },
  { value: 8, label: 'Last 8 weeks' },
  { value: 13, label: 'Last 13 weeks' },
  { value: 26, label: 'Last 26 weeks' },
  { value: 52, label: 'Last 52 weeks' },
]

const GROUP_OPTIONS = [
  { value: 'none', label: 'No Grouping' },
  { value: 'category', label: 'By Category' },
  { value: 'owner', label: 'By Owner' },
]

export default function ScoreboardFilters({ teamId, filters }: Props) {
  const [search, setSearch] = useState(filters.search)
  // The weekly grid already accepts twelve weeks. Expose that window without
  // removing the standalone board's existing longer windows or changing other
  // cadences' presets.
  const weeksOptions = filters.cadence === 'weekly'
    ? [...WEEKS_OPTIONS.slice(0, 2), { value: 12, label: 'Last 12 weeks' }, ...WEEKS_OPTIONS.slice(2)]
    : WEEKS_OPTIONS

  const applyFilter = (key: string, value: string | number) => {
    router.get(`/teams/${teamId}/scoreboard`, {
      ...filters,
      [key]: value,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['metrics', 'date_columns', 'filters'],
    })
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilter('search', search)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1 bg-charcoal/5 rounded-lg p-1">
        {CADENCE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => applyFilter('cadence', opt.value)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              filters.cadence === opt.value
                ? 'bg-white text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[200px] max-w-sm">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search metrics..."
              className="w-full pl-9 pr-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
            />
          </div>
        </form>

        <select
          aria-label="Weeks shown"
          value={filters.weeks}
          onChange={e => applyFilter('weeks', parseInt(e.target.value))}
          className="text-sm border border-stone/30 rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
        >
          {weeksOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <select
          value={filters.state}
          onChange={e => applyFilter('state', e.target.value)}
          className="text-sm border border-stone/30 rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
        >
          {STATE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {/* Cards view toggle deferred — to be added later */}
      </div>
    </div>
  )
}

export { GROUP_OPTIONS }
